import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { initializeErrorTracking } from './services/error-tracking';
import { BugReportWidget } from './components/feedback';
import { LoginPage } from './components/auth/LoginPage';
import { SuperAdminRoute } from './components/auth/SuperAdminRoute';
import { LandingPage, PrivacyPage, TermsPage, AcceptInvitePage } from './components/public';
import { TechDashboard } from './components/dashboard/TechDashboard';
import { ManagerDashboard } from './components/dashboard/ManagerDashboard';
import { ErrorBoundary, SectionErrorBoundary } from './components/error-boundary';
import {
  WorkorderDeckView,
  MY_SUBMITTED_CONFIG,
  MY_READY_FOR_PICKUP_CONFIG,
  MY_WORKORDER_RUN_CONFIG,
  MY_ASSIGNED_CONFIG,
} from './components/workorders';
import { AppShell } from './components/layout/AppShell';
import { useAuthStore } from './stores/authStore';
import { useImpersonationStore } from './stores/impersonationStore';
import { Button } from './components/ui/Button';

// Lazy-loaded components for code splitting
// These are loaded on-demand when the user navigates to their routes
const ScheduleEditorPage = lazy(() => import('./components/scheduling/ScheduleEditorPage').then(m => ({ default: m.ScheduleEditorPage })));
const DraftEditorPage = lazy(() => import('./components/routes/DraftEditorPage').then(m => ({ default: m.DraftEditorPage })));
const RouteDetailPage = lazy(() => import('./components/routes/RouteDetailPage').then(m => ({ default: m.RouteDetailPage })));
const MapExplorerPage = lazy(() => import('./components/maps/MapExplorerPage').then(m => ({ default: m.MapExplorerPage })));
const DailyReportPage = lazy(() => import('./components/dashboard/DailyReportPage').then(m => ({ default: m.DailyReportPage })));
const QAPage = lazy(() => import('./components/qa/QAPage').then(m => ({ default: m.QAPage })));
const QARecommendationsPage = lazy(() => import('./components/qa/QARecommendationsPage').then(m => ({ default: m.QARecommendationsPage })));
const DataTableDemo = lazy(() => import('./components/ui/DataTable/DataTableDemo').then(m => ({ default: m.DataTableDemo })));

// Admin pages - lazy loaded since they're less frequently accessed
const AdminLandingPage = lazy(() => import('./components/admin/AdminLandingPage').then(m => ({ default: m.AdminLandingPage })));
const AutomationsPage = lazy(() => import('./components/admin/AutomationsPage').then(m => ({ default: m.AutomationsPage })));
const RequestReportPage = lazy(() => import('./components/admin/RequestReportPage').then(m => ({ default: m.RequestReportPage })));
const UsersPage = lazy(() => import('./components/admin/UsersPage').then(m => ({ default: m.UsersPage })));
const AuditLogPage = lazy(() => import('./components/admin/AuditLogPage').then(m => ({ default: m.AuditLogPage })));
const ErrorAnalyticsPage = lazy(() => import('./components/admin/ErrorAnalyticsPage').then(m => ({ default: m.ErrorAnalyticsPage })));
const IssueTriagePage = lazy(() => import('./components/admin/IssueTriagePage').then(m => ({ default: m.IssueTriagePage })));
const ExceptionsReportPage = lazy(() => import('./components/admin/ExceptionsReportPage').then(m => ({ default: m.ExceptionsReportPage })));
const UserSimulatorPage = lazy(() => import('./components/admin/user-simulator').then(m => ({ default: m.UserSimulatorPage })));
const PriceListPage = lazy(() => import('./components/admin/PriceListPage').then(m => ({ default: m.PriceListPage })));
const SyncVerificationPage = lazy(() => import('./components/admin/SyncVerificationPage').then(m => ({ default: m.SyncVerificationPage })));
const QboMappingPage = lazy(() => import('./components/admin/QboMappingPage').then(m => ({ default: m.QboMappingPage })));
const SettingsPage = lazy(() => import('./components/settings/SettingsPage').then(m => ({ default: m.SettingsPage })));
const InventoryPage = lazy(() => import('./components/inventory/InventoryPage').then(m => ({ default: m.InventoryPage })));
const InventoryMigrationPage = lazy(() => import('./components/inventory/InventoryMigrationPage').then(m => ({ default: m.InventoryMigrationPage })));
const ClientInventoryPage = lazy(() => import('./components/inventory/ClientInventoryPage').then(m => ({ default: m.ClientInventoryPage })));
const TechInventoryPage = lazy(() => import('./components/inventory/TechInventoryPage').then(m => ({ default: m.TechInventoryPage })));

// Core pages - lazy loaded for code splitting
const WorkOrdersPage = lazy(() => import('./components/workorders/WorkOrdersPage').then(m => ({ default: m.WorkOrdersPage })));
const WorkordersCommandCenter = lazy(() => import('./components/workorders/WorkordersCommandCenter').then(m => ({ default: m.WorkordersCommandCenter })));
const UrgentWorkordersView = lazy(() => import('./components/workorders/UrgentWorkordersView').then(m => ({ default: m.UrgentWorkordersView })));
const ClientsPage = lazy(() => import('./components/clients/ClientsPage').then(m => ({ default: m.ClientsPage })));
const ClientDetailPage = lazy(() => import('./components/clients/ClientDetailPage').then(m => ({ default: m.ClientDetailPage })));
const AgingClientsView = lazy(() => import('./components/clients/AgingClientsView').then(m => ({ default: m.AgingClientsView })));
const NoNextVisitView = lazy(() => import('./components/clients/NoNextVisitView').then(m => ({ default: m.NoNextVisitView })));
const EmployeesPage = lazy(() => import('./components/employees/EmployeesPage').then(m => ({ default: m.EmployeesPage })));
const UnifiedSchedulesPage = lazy(() => import('./components/unified-schedules/UnifiedSchedulesPage').then(m => ({ default: m.UnifiedSchedulesPage })));
const TechDailyReport = lazy(() => import('./components/unified-schedules/TechDailyReport').then(m => ({ default: m.TechDailyReport })));
const ServiceLogsPage = lazy(() => import('./components/service-logs/ServiceLogsPage').then(m => ({ default: m.ServiceLogsPage })));
const ServiceLogNewPage = lazy(() => import('./components/service-logs/ServiceLogNewPage').then(m => ({ default: m.ServiceLogNewPage })));
const MissedServicesView = lazy(() => import('./components/service-logs/MissedServicesView').then(m => ({ default: m.MissedServicesView })));
const RoutesPage = lazy(() => import('./components/routes/RoutesPage').then(m => ({ default: m.RoutesPage })));
const RoutesNotScheduledView = lazy(() => import('./components/routes/RoutesNotScheduledView').then(m => ({ default: m.RoutesNotScheduledView })));
const ProfilePage = lazy(() => import('./components/profile/ProfilePage').then(m => ({ default: m.ProfilePage })));

// Run Planner - delivery run management
const RunPlannerPage = lazy(() => import('./components/runs/RunPlannerPage').then(m => ({ default: m.RunPlannerPage })));
const MyRunsPage = lazy(() => import('./components/runs/MyRunsPage').then(m => ({ default: m.MyRunsPage })));
const RunExecutionPage = lazy(() => import('./components/runs/RunExecutionPage').then(m => ({ default: m.RunExecutionPage })));

// Email page - lazy loaded
const EmailPage = lazy(() => import('./components/email/EmailPage').then(m => ({ default: m.EmailPage })));

// Management Modules - lazy loaded with their own internal routing
const FleetModule = lazy(() => import('./modules/fleet'));
const FinanceModule = lazy(() => import('./modules/finance'));
const CRMModule = lazy(() => import('./modules/crm'));
const TimeModule = lazy(() => import('./modules/time'));

// Initialize error tracking early (before any React components render)
initializeErrorTracking();

function App() {
  const { isInitialized } = useAuthStore();
  const initFromUrl = useImpersonationStore((state) => state.initFromUrl);

  // Initialize impersonation from URL parameter (for simulator iframe)
  useEffect(() => {
    initFromUrl();
  }, [initFromUrl]);

  // Show loading while Firebase Auth initializes
  if (!isInitialized) {
    return <LoadingScreen />;
  }

  return (
    <ErrorBoundary>
      <Toaster
        position="top-right"
        toastOptions={{
          className: 'font-sans',
          duration: 4000,
        }}
      />
      <BugReportWidget />
      <Routes>
        {/* Public routes (no auth required) */}
        <Route path="/welcome" element={<LandingPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/accept-invite" element={<AcceptInvitePage />} />
        {/* Dashboard route */}
        <Route path="/" element={<ProtectedRoute><AppShell><SectionErrorBoundary sectionName="Dashboard"><DashboardRouter /></SectionErrorBoundary></AppShell></ProtectedRoute>} />
        {/* Unified Schedules - consolidates Daily Schedules and Master Schedule */}
        <Route path="/schedules" element={<ProtectedRoute><AppShell><SectionErrorBoundary sectionName="Schedules"><LazyRoute><UnifiedSchedulesPage /></LazyRoute></SectionErrorBoundary></AppShell></ProtectedRoute>} />
        <Route path="/schedules/:view" element={<ProtectedRoute><AppShell><SectionErrorBoundary sectionName="Schedules"><LazyRoute><UnifiedSchedulesPage /></LazyRoute></SectionErrorBoundary></AppShell></ProtectedRoute>} />
        <Route path="/schedules/:view/:date" element={<ProtectedRoute><AppShell><SectionErrorBoundary sectionName="Schedules"><LazyRoute><UnifiedSchedulesPage /></LazyRoute></SectionErrorBoundary></AppShell></ProtectedRoute>} />
        {/* Tech daily report (printable) */}
        <Route path="/schedules/day/:date/report/:employeeId" element={<ProtectedRoute><LazyRoute><TechDailyReport /></LazyRoute></ProtectedRoute>} />
        {/* Daily Service Report */}
        <Route path="/daily-report" element={<ProtectedRoute><AppShell><LazyRoute><DailyReportPage /></LazyRoute></AppShell></ProtectedRoute>} />
        <Route path="/daily-report/:date" element={<ProtectedRoute><AppShell><LazyRoute><DailyReportPage /></LazyRoute></AppShell></ProtectedRoute>} />
        {/* Legacy redirects */}
        <Route path="/schedule" element={<Navigate to="/schedules" replace />} />
        <Route path="/schedule-editor" element={<ProtectedRoute><AppShell><LazyRoute><ScheduleEditorPage /></LazyRoute></AppShell></ProtectedRoute>} />
        {/* Entity pages with AppShell wrapper */}
        <Route path="/clients" element={<ProtectedRoute><AppShell><SectionErrorBoundary sectionName="Clients"><LazyRoute><ClientsPage /></LazyRoute></SectionErrorBoundary></AppShell></ProtectedRoute>} />
        <Route path="/clients/:id" element={<ProtectedRoute><AppShell><SectionErrorBoundary sectionName="Clients"><LazyRoute><ClientDetailPage /></LazyRoute></SectionErrorBoundary></AppShell></ProtectedRoute>} />
        <Route path="/workorders" element={<ProtectedRoute><AppShell><SectionErrorBoundary sectionName="WorkOrders"><LazyRoute><WorkordersCommandCenter /></LazyRoute></SectionErrorBoundary></AppShell></ProtectedRoute>} />
        <Route path="/workorders/viewer" element={<ProtectedRoute><AppShell><SectionErrorBoundary sectionName="WorkOrders"><LazyRoute><WorkOrdersPage /></LazyRoute></SectionErrorBoundary></AppShell></ProtectedRoute>} />
        <Route path="/team" element={<ProtectedRoute><AppShell><SectionErrorBoundary sectionName="Team"><LazyRoute><EmployeesPage /></LazyRoute></SectionErrorBoundary></AppShell></ProtectedRoute>} />
        <Route path="/service-logs" element={<ProtectedRoute><AppShell><SectionErrorBoundary sectionName="ServiceLogs"><LazyRoute><ServiceLogsPage /></LazyRoute></SectionErrorBoundary></AppShell></ProtectedRoute>} />
        <Route path="/service-logs/new" element={<ProtectedRoute><AppShell><SectionErrorBoundary sectionName="ServiceLogs"><LazyRoute><ServiceLogNewPage /></LazyRoute></SectionErrorBoundary></AppShell></ProtectedRoute>} />
        <Route path="/routes" element={<ProtectedRoute><AppShell><SectionErrorBoundary sectionName="Routes"><LazyRoute><RoutesPage /></LazyRoute></SectionErrorBoundary></AppShell></ProtectedRoute>} />
        <Route path="/routes/:routeId" element={<ProtectedRoute><AppShell><SectionErrorBoundary sectionName="Routes"><LazyRoute><RouteDetailPage /></LazyRoute></SectionErrorBoundary></AppShell></ProtectedRoute>} />
        <Route path="/routes/draft/new" element={<ProtectedRoute><AppShell><SectionErrorBoundary sectionName="DraftEditor"><LazyRoute><DraftEditorPage /></LazyRoute></SectionErrorBoundary></AppShell></ProtectedRoute>} />
        <Route path="/routes/draft/:draftId" element={<ProtectedRoute><AppShell><SectionErrorBoundary sectionName="DraftEditor"><LazyRoute><DraftEditorPage /></LazyRoute></SectionErrorBoundary></AppShell></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><AppShell><LazyRoute><ProfilePage /></LazyRoute></AppShell></ProtectedRoute>} />
        {/* Admin routes - lazy loaded, require Admin/SuperAdmin/Ops role */}
        <Route path="/admin" element={<AdminRoute><AppShell><LazyRoute><AdminLandingPage /></LazyRoute></AppShell></AdminRoute>} />
        <Route path="/admin/automations" element={<AdminRoute><AppShell><LazyRoute><AutomationsPage /></LazyRoute></AppShell></AdminRoute>} />
        <Route path="/admin/request-report" element={<AdminRoute><AppShell><LazyRoute><RequestReportPage /></LazyRoute></AppShell></AdminRoute>} />
        <Route path="/admin/users" element={<AdminRoute><AppShell><LazyRoute><UsersPage /></LazyRoute></AppShell></AdminRoute>} />
        <Route path="/admin/audit-log" element={<AdminRoute><AppShell><LazyRoute><AuditLogPage /></LazyRoute></AppShell></AdminRoute>} />
        <Route path="/admin/error-analytics" element={<SuperAdminRoute><AppShell><LazyRoute><ErrorAnalyticsPage /></LazyRoute></AppShell></SuperAdminRoute>} />
        <Route path="/admin/issue-triage" element={<SuperAdminRoute><AppShell><LazyRoute><IssueTriagePage /></LazyRoute></AppShell></SuperAdminRoute>} />
        <Route path="/admin/exceptions" element={<AdminRoute><AppShell><LazyRoute><ExceptionsReportPage /></LazyRoute></AppShell></AdminRoute>} />
        <Route path="/admin/user-simulator" element={<AdminRoute><LazyRoute><UserSimulatorPage /></LazyRoute></AdminRoute>} />
        <Route path="/admin/price-list" element={<AdminRoute><AppShell><LazyRoute><PriceListPage /></LazyRoute></AppShell></AdminRoute>} />
        <Route path="/admin/sync-verification" element={<AdminRoute><AppShell><LazyRoute><SyncVerificationPage /></LazyRoute></AppShell></AdminRoute>} />
        <Route path="/admin/qbo-mapping" element={<AdminRoute><AppShell><LazyRoute><QboMappingPage /></LazyRoute></AppShell></AdminRoute>} />
        <Route path="/admin/inventory" element={<AdminRoute><AppShell><LazyRoute><InventoryPage /></LazyRoute></AppShell></AdminRoute>} />
        <Route path="/admin/inventory/migration" element={<AdminRoute><AppShell><LazyRoute><InventoryMigrationPage /></LazyRoute></AppShell></AdminRoute>} />
        <Route path="/admin/settings" element={<AdminRoute><AppShell><LazyRoute><SettingsPage /></LazyRoute></AppShell></AdminRoute>} />
        <Route path="/qa-reports" element={<ProtectedRoute><AppShell><SectionErrorBoundary sectionName="QAReports"><LazyRoute><QAPage /></LazyRoute></SectionErrorBoundary></AppShell></ProtectedRoute>} />
        <Route path="/qa-recommendations" element={<ProtectedRoute><AppShell><SectionErrorBoundary sectionName="QAReports"><LazyRoute><QARecommendationsPage /></LazyRoute></SectionErrorBoundary></AppShell></ProtectedRoute>} />
        {/* Tech Inventory View - mobile-optimized for field techs */}
        <Route path="/inventory" element={<ProtectedRoute><LazyRoute><TechInventoryPage /></LazyRoute></ProtectedRoute>} />
        <Route path="/inventory/client/:clientId" element={<ProtectedRoute><AppShell><LazyRoute><ClientInventoryPage /></LazyRoute></AppShell></ProtectedRoute>} />
        <Route path="/inventory/:requestId" element={<ProtectedRoute><LazyRoute><TechInventoryPage /></LazyRoute></ProtectedRoute>} />
        {/* Demo routes - lazy loaded */}
        <Route path="/demo/datatable" element={<ProtectedRoute><AppShell><LazyRoute><DataTableDemo /></LazyRoute></AppShell></ProtectedRoute>} />
        {/* Workorder deck views */}
        <Route path="/urgent-workorders" element={<ProtectedRoute><AppShell><LazyRoute><UrgentWorkordersView /></LazyRoute></AppShell></ProtectedRoute>} />
        <Route path="/my-workorders" element={<ProtectedRoute><AppShell><WorkorderDeckView config={MY_SUBMITTED_CONFIG} /></AppShell></ProtectedRoute>} />
        <Route path="/my-run" element={<ProtectedRoute><AppShell><WorkorderDeckView config={MY_WORKORDER_RUN_CONFIG} /></AppShell></ProtectedRoute>} />
        <Route path="/ready-for-pickup" element={<ProtectedRoute><AppShell><WorkorderDeckView config={MY_READY_FOR_PICKUP_CONFIG} /></AppShell></ProtectedRoute>} />
        <Route path="/my-assigned" element={<ProtectedRoute><AppShell><WorkorderDeckView config={MY_ASSIGNED_CONFIG} /></AppShell></ProtectedRoute>} />
        {/* Client views */}
        <Route path="/aging-clients" element={<ProtectedRoute><AppShell><LazyRoute><AgingClientsView /></LazyRoute></AppShell></ProtectedRoute>} />
        <Route path="/no-next-visit" element={<ProtectedRoute><AppShell><LazyRoute><NoNextVisitView /></LazyRoute></AppShell></ProtectedRoute>} />
        <Route path="/client-map" element={<ProtectedRoute><AppShell><LazyRoute><MapExplorerPage /></LazyRoute></AppShell></ProtectedRoute>} />
        {/* Alert views */}
        <Route path="/routes-not-scheduled" element={<ProtectedRoute><AppShell><LazyRoute><RoutesNotScheduledView /></LazyRoute></AppShell></ProtectedRoute>} />
        <Route path="/missed-services" element={<ProtectedRoute><AppShell><LazyRoute><MissedServicesView /></LazyRoute></AppShell></ProtectedRoute>} />
        {/* Run Planner - Delivery run management */}
        <Route path="/runs/planner" element={<ProtectedRoute><LazyRoute><RunPlannerPage /></LazyRoute></ProtectedRoute>} />
        <Route path="/my-runs" element={<ProtectedRoute><LazyRoute><MyRunsPage /></LazyRoute></ProtectedRoute>} />
        <Route path="/runs/:runName/execute" element={<ProtectedRoute><LazyRoute><RunExecutionPage /></LazyRoute></ProtectedRoute>} />
        {/* Email */}
        <Route path="/email" element={<ProtectedRoute><AppShell><SectionErrorBoundary sectionName="Email"><LazyRoute><EmailPage /></LazyRoute></SectionErrorBoundary></AppShell></ProtectedRoute>} />
        {/* Management Modules - lazy loaded with internal routing */}
        <Route path="/fleet/*" element={<ProtectedRoute><AppShell><SectionErrorBoundary sectionName="Fleet"><LazyRoute><FleetModule /></LazyRoute></SectionErrorBoundary></AppShell></ProtectedRoute>} />
        <Route path="/finance/*" element={<ProtectedRoute><AppShell><SectionErrorBoundary sectionName="Finance"><LazyRoute><FinanceModule /></LazyRoute></SectionErrorBoundary></AppShell></ProtectedRoute>} />
        <Route path="/crm/*" element={<ProtectedRoute><AppShell><SectionErrorBoundary sectionName="CRM"><LazyRoute><CRMModule /></LazyRoute></SectionErrorBoundary></AppShell></ProtectedRoute>} />
        <Route path="/time/*" element={<ProtectedRoute><AppShell><SectionErrorBoundary sectionName="Time"><LazyRoute><TimeModule /></LazyRoute></SectionErrorBoundary></AppShell></ProtectedRoute>} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </ErrorBoundary>
  );
}

// Loading screen while Firebase Auth initializes
function LoadingScreen() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-forest-900 via-forest-800 to-forest-950 flex flex-col items-center justify-center">
      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 shadow-glow-lg flex items-center justify-center mb-4 animate-pulse">
        <span className="text-3xl">🌿</span>
      </div>
      <p className="text-forest-300">Loading...</p>
    </div>
  );
}

// Page loading fallback for lazy-loaded routes (shows within AppShell)
function PageLoadingFallback() {
  return (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center animate-pulse">
          <span className="text-xl">🌿</span>
        </div>
        <p className="text-sage-500 text-sm">Loading page...</p>
      </div>
    </div>
  );
}

// Wrapper for lazy-loaded components with Suspense
function LazyRoute({ children }: { children: ReactNode }) {
  return <Suspense fallback={<PageLoadingFallback />}>{children}</Suspense>;
}

// Protects routes that require authentication
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Protects admin routes that require Admin/SuperAdmin/Ops role
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isSuperAdmin, getPrimaryRole } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const role = getPrimaryRole();
  const isAdmin = isSuperAdmin || role === 'SuperAdmin' || role === 'Admin' || role === 'Ops';

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

// Routes to the appropriate dashboard based on user role
function DashboardRouter() {
  const { getPrimaryRole } = useAuthStore();
  const role = getPrimaryRole();

  switch (role) {
    case 'SuperAdmin':
    case 'Admin':
    case 'Ops':
      return <ManagerDashboard />;
    case 'Tech':
    case 'Holiday':
      return <TechDashboard />;
    default:
      return <TechDashboard />;
  }
}

function NotFoundPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gradient-to-br from-forest-900 via-forest-800 to-forest-950 flex flex-col items-center justify-center p-4">
      <h1 className="font-display text-8xl font-bold text-forest-600/30">404</h1>
      <p className="text-xl text-forest-300 mt-4">Page not found</p>
      <Button
        variant="primary"
        className="mt-8"
        onClick={() => navigate('/')}
      >
        Go Home
      </Button>
    </div>
  );
}

export default App;
